import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, ArrowLeft, BookOpen } from "lucide-react";
import { Seo, breadcrumbSchema } from "../components/Seo";
import { ServiceCTA } from "../components/ServiceCTA";

const fmtDate = (ts) =>
  ts ? new Date(ts * 1000).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "";

const BlogPost = () => {
  const { slug }              = useParams();
  const [post, setPost]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    fetch(`/api/posts.php?slug=${encodeURIComponent(slug)}`)
      .then((r) => {
        if (r.status === 404) { setNotFound(true); return null; }
        return r.json();
      })
      .then((d) => { if (d?.post) setPost(d.post); })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-400">
        Loading…
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-6 py-20">
        <BookOpen className="w-12 h-12 text-slate-300 mb-4" />
        <h1 className="text-2xl font-bold text-navy mb-2">Post not found</h1>
        <p className="text-slate-500 mb-6">This article doesn't exist or has been removed.</p>
        <Link to="/blog" className="bg-navy text-white px-5 py-3 rounded-xl font-semibold hover:bg-navy-light transition-colors">
          Back to Blog
        </Link>
      </div>
    );
  }

  return (
    <>
      <Seo
        title={post.metaTitle || post.title}
        description={post.metaDescription || post.excerpt || ""}
        ogTitle={post.metaTitle || post.title}
        ogDescription={post.metaDescription || post.excerpt || ""}
        ogImage={post.featuredImage || undefined}
        path={`/blog/${post.slug}`}
        schema={[
          breadcrumbSchema([
            { name: "Blog", path: "/blog" },
            { name: post.title, path: `/blog/${post.slug}` },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.title,
            description: post.metaDescription || post.excerpt,
            datePublished: post.createdAt
              ? new Date(post.createdAt * 1000).toISOString()
              : undefined,
            dateModified: post.updatedAt
              ? new Date(post.updatedAt * 1000).toISOString()
              : undefined,
            author: {
              "@type": "Organization",
              name: "Prime Tenancy Cleaning",
              url: "https://primetenancycleaning.com",
            },
            publisher: {
              "@type": "Organization",
              name: "Prime Tenancy Cleaning",
              url: "https://primetenancycleaning.com",
            },
            image: post.featuredImage
              ? `https://primetenancycleaning.com${post.featuredImage}`
              : "https://primetenancycleaning.com/images/kitchen-after.jpg",
          },
        ]}
      />

      {/* Hero */}
      <section className="relative pt-28 pb-14 md:pt-40 md:pb-20 bg-navy overflow-hidden">
        <div className="absolute inset-0">
          {post.featuredImage && (
            <img src={post.featuredImage} alt="" className="w-full h-full object-cover opacity-20" />
          )}
          <div className="absolute inset-0 bg-navy/90" />
        </div>
        <div className="relative z-10 container mx-auto px-6 md:px-12 lg:px-20 max-w-4xl">
          <Link to="/blog" className="inline-flex items-center gap-2 text-slate-400 hover:text-gold mb-8 text-sm font-medium transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            {post.focusKeyword && (
              <span className="inline-block bg-gold/20 text-gold px-3 py-1.5 rounded-full text-xs font-semibold mb-4 uppercase tracking-wide">
                {post.focusKeyword}
              </span>
            )}
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
              {post.title}
            </h1>
            {post.excerpt && (
              <p className="text-slate-300 text-lg leading-relaxed mb-6 max-w-2xl">{post.excerpt}</p>
            )}
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <Calendar className="w-4 h-4" />
              {fmtDate(post.createdAt)}
              {post.updatedAt && post.updatedAt !== post.createdAt && (
                <span className="ml-2">· Updated {fmtDate(post.updatedAt)}</span>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          <div className={post.featuredImage ? "max-w-5xl mx-auto lg:flex lg:gap-12 lg:items-start" : "max-w-3xl mx-auto"}>
            {/* Main prose */}
            <div className="flex-1 min-w-0">
              <div
                className="prose prose-lg prose-slate max-w-none
                  prose-headings:font-heading prose-headings:text-navy
                  prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                  prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
                  prose-p:text-slate-700 prose-p:leading-relaxed
                  prose-a:text-teal prose-a:no-underline hover:prose-a:text-navy
                  prose-strong:text-navy
                  prose-li:text-slate-700
                  prose-img:rounded-2xl prose-img:shadow-sm"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />
              <div className="mt-12 pt-8 border-t border-slate-200">
                <Link to="/blog" className="inline-flex items-center gap-2 text-teal hover:text-navy font-semibold transition-colors">
                  <ArrowLeft className="w-4 h-4" /> Back to all articles
                </Link>
              </div>
            </div>

            {/* Featured image — right column */}
            {post.featuredImage && (
              <div className="lg:w-80 xl:w-96 flex-shrink-0 mt-10 lg:mt-0 lg:sticky lg:top-28 self-start">
                <div className="rounded-2xl overflow-hidden shadow-sm border border-slate-100">
                  <img
                    src={post.featuredImage}
                    alt={post.title}
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <ServiceCTA
        title="Ready to book your London end of tenancy clean?"
        description="Fixed price, 7-day re-clean guarantee, all 32 London boroughs covered."
        variant="navy"
      />
    </>
  );
};

export default BlogPost;
